<script lang="ts">
  import ProductGrid from '$components/product-grid.svelte';
  import type { ProductCardFragment } from '$gql/storefront/operations';
  import type { LanguageCode, ProductRecommendationIntent } from '$gql/storefront/schemas';
  import { isProductRecommendationIntent } from '$gql/storefront/utils';
  import { storyblokEditable } from '$lib/actions/storyblok-editable';
  import { getPDPProductContext } from '$lib/contexts/pdp-product';
  import { shopify } from '$lib/shopify';
  import type { ProductRecommendationsStoryblok } from '$types/bloks';
  import { createQueries, createQuery } from '@tanstack/svelte-query';
  import { nostoRecommendations } from '$lib/nosto';
  import { lang } from '$lib/lang';
  import { optimalUSCountry, parseCountryAsCode } from '$lib/country';

  export let content: ProductRecommendationsStoryblok;
  export let referenceProductId: string | undefined = undefined;

  /* GraphQL */ `
    query getAutomaticRecommendations(
      $productId: ID!
      $intent: ProductRecommendationIntent
      $lang: LanguageCode
      $country: CountryCode
    ) @inContext(language: $lang, country: $country) {
      productRecommendations(productId: $productId, intent: $intent) {
        ...ProductCard
      }
    }

    query getProductCards($ids: [ID!]!, $lang: LanguageCode, $country: CountryCode)
    @inContext(language: $lang, country: $country) {
      nodes(ids: $ids) {
        __typename
        ... on Product {
          ...ProductCard
        }
      }
    }
  `;

  // context created in product-detail-page.svelte
  const pdpContext = getPDPProductContext();
  const productId = pdpContext?.id || referenceProductId;

  // slugs for manual recommendations. defined within the content's content.
  const handles = (content.products || [])
    .map((product) => (typeof product !== 'string' ? product.slug : null))
    .filter(Boolean) as string[];

  const queries = createQueries(
    handles.map((handle) => ({
      staleTime: 5 * 60 * 1000,
      queryKey: ['productCard', handle],
      queryFn: async () => {
        return await $shopify.getProductCard({
          handle,
          lang: $lang.toUpperCase() as LanguageCode,
          country: parseCountryAsCode($optimalUSCountry)
        });
      }
    }))
  );

  let containerWidth = 0;
  let screenSize = 0;

  $: automaticRecommendations = createQuery({
    staleTime: 5 * 60 * 1000,
    enabled: !!productId && isProductRecommendationIntent(content.automatic_recommendations),
    queryKey: ['automatic-recommendations', productId, content.automatic_recommendations],
    queryFn: async () => {
      return await $shopify.getAutomaticRecommendations({
        productId: productId as string,
        intent: content.automatic_recommendations as ProductRecommendationIntent,
        lang: $lang.toUpperCase() as LanguageCode,
        country: parseCountryAsCode($optimalUSCountry)
      });
    }
  });

  $: nostoRecommendationsIds = $nostoRecommendations.map((item) => {
    return `gid://shopify/Product/${item.productId}`;
  });

  $: nostoRecommendationsQuery = createQuery({
    staleTime: 5 * 60 * 1000,
    enabled: !!productId && content.automatic_recommendations === 'NOSTO_RELATED',
    queryKey: [
      'nosto-recommendations',
      productId,
      nostoRecommendationsIds,
      content.automatic_recommendations === 'NOSTO_RELATED'
    ],
    queryFn: async () => {
      const relatedProductsCards = await $shopify.getProductCards({
        ids: nostoRecommendationsIds.map((id: string) => btoa(id)),
        lang: $lang.toUpperCase() as LanguageCode,
        country: parseCountryAsCode($optimalUSCountry)
      });

      return relatedProductsCards.nodes || [];
    }
  });

  $: productsArr = [
    ...($queries || []).map((query) => query.data?.product),
    ...($automaticRecommendations.data?.productRecommendations || []),
    ...($nostoRecommendationsQuery.data || [])
  ];
  $: products = productsArr.filter(Boolean) as ProductCardFragment[];
</script>

<svelte:window bind:innerWidth={screenSize} />

<div
  use:storyblokEditable={content}
  class="py-2 lg:py-8 lg:pt-0"
  style:--x-margin={`${(screenSize - containerWidth) / 2 || 16}px`}
>
  <div aria-hidden="true" class="container mx-auto" bind:clientWidth={containerWidth} />
  <ProductGrid
    {products}
    loading={($automaticRecommendations.data && $automaticRecommendations.isLoading) ||
      $queries.some((q) => q.isLoading)}
  />
</div>
